/**
 * api/accessMatrix.js — queries e mutations para a tela de Matriz de Acessos.
 *
 * Endpoints cobertos (F0-BE-2):
 *   GET  /access-matrix/matrices                        — lista matrizes (perm access_matrix:view)
 *   GET  /access-matrix/matrices/{id}/permissions       — permissões de uma matriz
 *   PUT  /access-matrix/matrices/{id}/permissions       — salvar permissões (perm access_matrix:administer)
 *   GET  /access-matrix/users                           — lista usuários com matriz associada
 *   POST /access-matrix/users                           — cadastrar usuário com perfil (perm access_matrix:administer)
 *   POST /access-matrix/users/{user_id}/matrix          — reatribuir usuário à matriz (perm access_matrix:administer)
 *
 * Convenções:
 *   - Query keys seguem o padrão ['access-matrix', <recurso>, <params>] para
 *     invalidação granular após mutations.
 *   - Mutations invalida 'access-matrix' inteiro para simplicidade de Fase 0;
 *     refinar por recurso quando o volume de dados justificar.
 *   - Erros ApiError com status 403 são propagados — o componente exibe banner.
 */
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useApi } from './client';

// ─── Helpers de normalização ──────────────────────────────────────────────────

/**
 * O backend retorna as permissões como uma lista plana de objetos
 * { resource, action, allowed }. Para a grade visual precisamos de um mapa
 * resource → action → boolean que espelha o `grid` do protótipo.
 *
 * @param {Array<{resource: string, action: string, allowed: boolean}>} permissions
 * @returns {Record<string, Record<string, boolean>>}
 */
export function permissionsToGrid(permissions) {
  const grid = {};
  for (const { resource, action, allowed } of permissions) {
    if (!grid[resource]) grid[resource] = {};
    grid[resource][action] = allowed;
  }
  return grid;
}

/**
 * Converte o grid visual de volta para a lista plana que o PUT espera.
 *
 * @param {Record<string, Record<string, boolean>>} grid
 * @returns {Array<{resource: string, action: string, allowed: boolean}>}
 */
export function gridToPermissions(grid) {
  const list = [];
  for (const [resource, actions] of Object.entries(grid)) {
    for (const [action, allowed] of Object.entries(actions)) {
      list.push({ resource, action, allowed });
    }
  }
  return list;
}

// ─── Query: lista de matrizes ─────────────────────────────────────────────────

/**
 * useMatrices — lista todas as matrizes do tenant.
 *
 * A resposta esperada do backend:
 *   Array<{ id, name, color, description, is_system, user_count, permission_count }>
 *
 * @returns {import('@tanstack/react-query').UseQueryResult}
 */
export function useMatrices() {
  const api = useApi();
  return useQuery({
    queryKey: ['access-matrix', 'matrices'],
    queryFn: async () => {
      // Normaliza o contrato do backend (display_name/slug/color_token) para o
      // shape que a tela usa (id/name/slug/color).
      const raw = await api.get('/access-matrix/matrices');
      const list = Array.isArray(raw) ? raw : raw?.items ?? raw?.matrices ?? [];
      return list.map((m) => ({
        ...m,
        id: m.id ?? m.matrix_id,
        name: m.display_name ?? m.name ?? m.slug ?? '—',
        slug: m.slug ?? m.matrix_slug,
        color: m.color ?? m.color_token,
      }));
    },
    staleTime: 60_000,
  });
}

// ─── Query: permissões de uma matriz ─────────────────────────────────────────

/**
 * useMatrixPermissions — permissões de uma matriz específica, normalizadas em grid.
 *
 * @param {string} matrixId
 * @returns {import('@tanstack/react-query').UseQueryResult}
 */
export function useMatrixPermissions(matrixId) {
  const api = useApi();
  return useQuery({
    queryKey: ['access-matrix', 'permissions', matrixId],
    queryFn: async () => {
      const raw = await api.get(`/access-matrix/matrices/${matrixId}/permissions`);
      // raw: Array<{resource, action, allowed}> ou { permissions: [...] }
      const list = Array.isArray(raw) ? raw : raw.permissions ?? [];
      return permissionsToGrid(list);
    },
    enabled: Boolean(matrixId),
    staleTime: 60_000,
  });
}

// ─── Query: usuários ──────────────────────────────────────────────────────────

/**
 * useAccessUsers — lista todos os usuários do tenant com matriz associada.
 *
 * Resposta esperada:
 *   Array<{ id, name, email, role, matrix_id }>
 *
 * @returns {import('@tanstack/react-query').UseQueryResult}
 */
export function useAccessUsers() {
  const api = useApi();
  return useQuery({
    queryKey: ['access-matrix', 'users'],
    queryFn: async () => {
      // Normaliza (user_id/display_name/role_label) para (id/name/role).
      const raw = await api.get('/access-matrix/users');
      const list = Array.isArray(raw) ? raw : raw?.items ?? raw?.users ?? [];
      return list.map((u) => ({
        ...u,
        id: u.id ?? u.user_id,
        name: u.display_name ?? u.name ?? u.email ?? '—',
        role: u.role ?? u.role_label ?? '',
        matrix_id: u.matrix_id,
      }));
    },
    staleTime: 60_000,
  });
}

// ─── Mutation: salvar permissões de uma matriz ────────────────────────────────

/**
 * useSavePermissions — PUT /access-matrix/matrices/{id}/permissions
 *
 * Payload: { permissions: Array<{resource, action, allowed}> }
 * Requer perm access_matrix:administer (Admin).
 *
 * Invalida as queries de matrizes e permissões após sucesso.
 *
 * @returns {import('@tanstack/react-query').UseMutationResult}
 */
export function useSavePermissions() {
  const api = useApi();
  const qc  = useQueryClient();

  return useMutation({
    mutationFn: ({ matrixId, grid }) =>
      api.put(`/access-matrix/matrices/${matrixId}/permissions`, {
        permissions: gridToPermissions(grid),
      }),
    onSuccess: (_data, { matrixId }) => {
      // Invalida cache da matriz editada e da lista (permission_count pode ter mudado)
      qc.invalidateQueries({ queryKey: ['access-matrix', 'permissions', matrixId] });
      qc.invalidateQueries({ queryKey: ['access-matrix', 'matrices'] });
    },
  });
}

// ─── Mutation: reatribuir usuário a uma matriz ────────────────────────────────

/**
 * useAssignUserMatrix — POST /access-matrix/users/{user_id}/matrix
 *
 * Payload: { matrix_id: string }
 * Requer perm access_matrix:administer (Admin).
 *
 * @returns {import('@tanstack/react-query').UseMutationResult}
 */
export function useAssignUserMatrix() {
  const api = useApi();
  const qc  = useQueryClient();

  return useMutation({
    mutationFn: ({ userId, matrixId }) =>
      api.post(`/access-matrix/users/${userId}/matrix`, { matrix_id: matrixId }),
    onSuccess: () => {
      // Invalida lista de usuários (matrix_id do usuário mudou)
      qc.invalidateQueries({ queryKey: ['access-matrix', 'users'] });
      // Invalida matrizes porque user_count pode ter mudado
      qc.invalidateQueries({ queryKey: ['access-matrix', 'matrices'] });
    },
  });
}

// ─── Mutation: cadastrar usuário ──────────────────────────────────────────────

/**
 * useCreateUser — POST /access-matrix/users
 *
 * Payload: { email, display_name, role_label, matrix_id }
 * Requer perm access_matrix:administer (Admin). E-mail repetido → 409.
 * A pessoa entra depois pelo SSO com o mesmo e-mail; não há senha.
 *
 * @returns {import('@tanstack/react-query').UseMutationResult}
 */
export function useCreateUser() {
  const api = useApi();
  const qc  = useQueryClient();

  return useMutation({
    mutationFn: ({ email, displayName, roleLabel, matrixId }) =>
      api.post('/access-matrix/users', {
        email,
        display_name: displayName,
        role_label: roleLabel ?? '',
        matrix_id: matrixId,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['access-matrix', 'users'] });
      // user_count da matriz escolhida mudou
      qc.invalidateQueries({ queryKey: ['access-matrix', 'matrices'] });
    },
  });
}
